import DetailsTab from './components/DetailsTab.vue'
import MetadataTab from './components/MetadataTab.vue'
import RawDocumentTab from './components/RawDocumentTab.vue'
import HistoryTab from './components/HistoryTab.vue'
import MatchingRecordsTab from './components/MatchingRecordsTab.vue'
import recallReviewServices from '../../../../services/notisphere/recallReview'

export default {
    name: 'RecallReviewModal',
    components: {
        DetailsTab,
        MetadataTab,
        RawDocumentTab,
        HistoryTab,
        MatchingRecordsTab,
    },
    props: {
        reviewId: {
            type: String,
            default: null,
        },
        currentIndex: {
            type: Number,
            default: 0,
        },
        totalRecords: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            recall_id: null,
            recallData: null,
            isLoading: false,
            activeTab: 0,
            hasUnsavedChanges: false,
            hasSavedChanges: false, // Track if changes were saved during this session
            pendingNavigation: null,
            isFieldBeingEdited: false,
            isMandatoryFieldsValid: true,
            changeLogs: [],
            originalData: null,
            validationErrors: [],
            activeToasts: new Set(), // Track active toast messages
            criticalFieldsModified: [], // Track which critical fields have been modified
            // Find Original modal state
            findOriginalSearchQuery: '',
            findOriginalResults: [],
            findOriginalSearching: false,
            selectedTargetOriginal: null,
            linkAction: 'moveAll',
            promotedChildId: null,
            showNoSearchResultsMessage: false,
        }
    },
    computed: {
        hasDuplicateChildren() {
            return this.recallData?.duplicates_data && this.recallData.duplicates_data.length > 0
        },
        promoteChildOptions() {
            if (!this.recallData?.duplicates_data?.length) return []
            return this.recallData.duplicates_data.map((dup, idx) => {
                let name = dup.recall_name || dup.recalling_firm || `Duplicate ${idx + 1}`
                if (name.length > 30) {
                    name = name.substring(0, 30) + '...'
                }
                const createdDate = this.formatDate(dup.created_at || dup.provider_creation_date)
                const label = createdDate && createdDate !== '-' ? `${name} (Created: ${createdDate})` : name
                const id = dup.duplicate_recall_id || dup._id
                return { id, label }
            })
        },
        canConfirmFindOriginal() {
            // Must always select a searched Original (target) before confirming — including Promote a Child
            const hasSelectedOriginal = this.selectedTargetOriginal && (this.selectedTargetOriginal._id || this.selectedTargetOriginal.id)
            if (!hasSelectedOriginal) return false
            if (this.linkAction === 'promoteChild') {
                return !!this.promotedChildId
            }
            return true
        },
        statusBadgeClass() {
            if (!this.recallData) return 'badge-secondary'
            const statusMap = {
                PUBLISHED: 'badge-success',
                PENDING: 'badge-warning',
            }
            return statusMap[this.recallData.status] || 'badge-secondary'
        },
        // Check if any critical fields have been modified
        hasCriticalFieldChanges() {
            const criticalFields = ['recalling_firm', 'recall_date', 'recall_name']
            return this.criticalFieldsModified.some(field => criticalFields.includes(field))
        },
    },
    watch: {
        reviewId: {
            handler(newReviewId) {
                if (newReviewId) {
                    this.loadReviewData()
                }
            },
            immediate: true,
        },
        linkAction(newVal) {
            if (newVal === 'promoteChild' && this.promoteChildOptions.length && !this.promotedChildId) {
                this.promotedChildId = this.promoteChildOptions[0].id
            }
        },
    },
    methods: {
        show(id, currentIndex = null, totalRecords = null) {
            this.recall_id = id
            this.hasSavedChanges = false // Reset saved changes flag when opening modal
            this.$refs.modal.show()
            this.loadReviewData()
            this.$emit('shown')
        },

        hide() {
            this.$refs.modal.hide()
        },

        onModalHide(bvModalEvent) {
            if (this.hasUnsavedChanges && bvModalEvent.trigger !== 'ok' && bvModalEvent.trigger !== 'headerclose') {
                bvModalEvent.preventDefault()
                this.$refs.confirmCloseModal.show()
            }
        },

        handleCancel() {
            if (this.hasUnsavedChanges) {
                // Show confirmation modal if there are unsaved changes
                this.$refs.confirmCloseModal.show()
            } else {
                // Close directly if no unsaved changes
                this.hide()
            }
        },

        confirmClose() {
            // User clicked Yes - discard changes and close
            this.hasUnsavedChanges = false
            this.hide()
        },

        cancelClose() {
            // User clicked No - do nothing, stay on the modal
            // Modal will close automatically by b-modal
        },

        confirmNavigation() {
            // User clicked Yes - discard changes and navigate
            this.hasUnsavedChanges = false
            if (this.pendingNavigation === 'next') {
                this.$emit('nextRecord')
            } else if (this.pendingNavigation === 'previous') {
                this.$emit('previousRecord')
            }
            this.pendingNavigation = null
        },

        cancelNavigation() {
            // User clicked No - cancel navigation
            this.pendingNavigation = null
        },

        // Handle editing state changes from DetailsTab
        handleEditingStateChanged(isEditing) {
            this.isFieldBeingEdited = isEditing
        },

        // Handle validation state changes from DetailsTab
        handleValidationStateChanged(isValid) {
            this.isMandatoryFieldsValid = isValid
        },

        onHidden() {
            this.activeTab = 0
            this.recallData = null
            this.hasUnsavedChanges = false
            this.criticalFieldsModified = [] // Reset critical fields tracking
            // Only refresh grid if changes were saved during this session
            if (this.hasSavedChanges) {
                this.$emit('refresh-data')
            }
            this.hasSavedChanges = false // Reset for next time
            this.$emit('hidden')
        },

        getStatusClass(status) {
            const statusMap = {
                'PUBLISHED': 'badge-success',
                'PENDING': 'badge-warning',
                'Original': 'badge-success',
                'Duplicate': 'badge-danger',
                'Possible Duplicate': 'badge-warning',
                'Pending Verification': 'badge-secondary',
            }
            return statusMap[status] || 'badge-secondary'
        },

        async loadReviewData() {
            if (!this.recall_id) {
                console.warn('No recall_id provided')
                return
            }

            this.hasUnsavedChanges = false

            try {
                const response = await recallReviewServices.getRecallReviewById(this.recall_id)
                this.recallData = response.data.d.data
                // Store original data for change tracking
                this.originalData = JSON.parse(JSON.stringify(this.recallData))
                this.changeLogs = [] // Reset change logs when loading new data
            } catch (error) {
                console.error('Error loading recall review data:', error)
                this._showToast('Failed to load recall review data', { variant: 'danger' })
            } finally {
                this.isLoading = false
            }
        },

        // Refresh data
        async refreshData() {
            await this.loadReviewData()
        },

        // Create change log entry
        createChangeLog(field, action, oldValue, newValue) {
            // Get user info from localStorage or Vuex store
            var user = this.$store.state.user
            const userEmail = user?.email || 'unknown@user.com'
            const userId = user?.id || null
            const userName = user?.full_name || 'Unknown User'

            const changeLog = {
                field: field,
                action: action, // 'add', 'update', 'delete'
                oldValue: oldValue !== undefined && oldValue !== null ? String(oldValue) : '',
                newValue: newValue !== undefined && newValue !== null ? String(newValue) : '',
                updated_by: userEmail,
                timestamp: new Date().toISOString(),
                updated_user_id: userId ? parseInt(userId) : null,
                updated_user_name: userName
            }

            this.changeLogs.push(changeLog)
        },

        // Handle save action
        async handleSave() {
            if (!this.recallData) return

            try {
                this.isLoading = true

                // Add change logs to the payload
                const payload = {
                    ...this.recallData,
                    changelog: this.changeLogs
                }

                // Call API to update recall review with all data including change logs
                const response = await recallReviewServices.updateRecallReview(
                    this.recallData._id,
                    payload
                )

                this._showToast('Changes saved successfully')

                this.hasUnsavedChanges = false
                this.hasSavedChanges = true // Mark that changes were saved during this session
                this.criticalFieldsModified = [] // Reset critical fields tracking
                // Reset change logs and validation errors after save
                this.changeLogs = []
                this.validationErrors = []
                this.$emit('saved', this.recallData)
                // Reload data from server to get latest changelog
                await this.loadReviewData()
            } catch (error) {
                console.error('Error saving changes:', error)
                this._showToast('Failed to save changes', { variant: 'danger' })
            } finally {
                this.isLoading = false
            }
        },

        handleResolve() {
            // Check if there are unsaved changes
            if (this.hasUnsavedChanges) {
                this.$refs.confirmResolveModal.show()
                return
            }
            // Proceed with resolve
            this.proceedWithResolve()
        },

        // Proceed with resolve (no unsaved changes check)
        proceedWithResolve() {
            this.$refs.resolveModal.show()
        },

        // Hide resolve modal
        hideResolveModal() {
            this.$refs.resolveModal.hide()
        },

        async handleMarkAsOriginal() {
            // Check if any critical fields were updated
            const criticalFields = ['recalling_firm', 'recall_date', 'recall_name']
            let hasCriticalFieldUpdates = false

            // Check if any critical field has been modified from the original data
            for (const field of criticalFields) {
                const currentValue = this.recallData?.recall_info?.[field]
                const originalValue = this.originalData?.recall_info?.[field]

                // Normalize values for comparison
                const currentNormalized = currentValue === undefined || currentValue === null ? '' : String(currentValue)
                const originalNormalized = originalValue === undefined || originalValue === null ? '' : String(originalValue)

                if (currentNormalized !== originalNormalized) {
                    hasCriticalFieldUpdates = true
                    break
                }
            }

            // If no critical fields were updated, show error and prevent marking as Original
            if (!hasCriticalFieldUpdates) {
                this._showToast('Please update at least one critical field (Recalling Firm, Recall Date, or Recall Name) before marking as Original', {
                    variant: 'warning',
                    toaster: 'b-toaster-top-center',
                    autoHideDelay: 5000
                })
                return
            }

            // Add change log entry for originality change
            const oldOriginality = this.recallData.originality || 'Possible Duplicate'
            const newOriginality = 'Original'
            // Create change log entry for resolve action
            this.createChangeLog('originality', 'resolve', oldOriginality, newOriginality)
            // Add change logs to the payload
            const payload = {
                originality: newOriginality,
                changelog: this.changeLogs
            }
            const response = await recallReviewServices.recallReviewOriginalityStats(this.recallData._id, 'Original', payload)
            if( !response.error ){
                this._showToast('Marked as Original', { variant: 'success' })
                // Reset change logs after successful update
                this.changeLogs = []
                this.hasSavedChanges = true // Mark that changes were made (originality changed)
                this.refreshData()
            }
        },

        async handleMarkAsDuplicate() {
            // Add change log entry for originality change
            const oldOriginality = this.recallData.originality || 'Possible Duplicate'
            const newOriginality = 'Duplicate'
            // Create change log entry for resolve action
            this.createChangeLog('originality', 'resolve', oldOriginality, newOriginality)
            this.createChangeLog('status', 'resolve', this.recallData.status, 'COMPLETED')
            // Add change logs to the payload
            const payload = {
                originality: newOriginality,
                changelog: this.changeLogs
            }
            const response = await recallReviewServices.recallReviewOriginalityStats(this.recallData._id, 'Duplicate', payload)
            if( !response.error ){
                this._showToast('Marked as Duplicate.', { variant: 'success', toaster: 'b-toaster-top-center', autoHideDelay: 5000 })
                // Reset change logs after successful update
                this.changeLogs = []
                this.hasSavedChanges = true // Mark that changes were made (originality changed)
                this.refreshData()
            }
        },

        async handleRevertDuplicate() {
            if (!this.recallData?._id || this.recallData.originality !== 'Duplicate') return
            try {
                this.isLoading = true
                const oldOriginality = this.recallData.originality || 'Duplicate'
                const oldStatus = this.recallData.status || 'COMPLETED'
                this.createChangeLog('originality', 'revert', oldOriginality, 'Possible Duplicate')
                this.createChangeLog('status', 'revert', oldStatus, 'PENDING')
                const payload = { changelog: this.changeLogs }
                const response = await recallReviewServices.revertDuplicateToPossibleDuplicate(this.recallData._id, payload)
                if (!response.error) {
                    this._showToast('Recall reverted to Possible Duplicate. You can now resolve it.', {
                        variant: 'success',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 5000
                    })
                    this.changeLogs = []
                    this.hasSavedChanges = true
                    await this.refreshData()
                } else {
                    this._showToast(response.message || 'Failed to revert duplicate', { variant: 'danger' })
                }
            } catch (e) {
                console.error('Revert duplicate error:', e)
                this._showToast('Failed to revert duplicate', { variant: 'danger' })
            } finally {
                this.isLoading = false
            }
        },

        duplicateConfirmation() {
            this.$refs.resolveModal.hide()
            this.$refs.resolveConfirmModal.show()
        },

        openFindOriginalModal() {
            this.findOriginalSearchQuery = ''
            this.findOriginalResults = []
            this.findOriginalSearching = false
            this.selectedTargetOriginal = null
            this.linkAction = 'moveAll'
            this.promotedChildId = null
            this.$nextTick(() => {
                this.$refs.findOriginalModal.show()
            })
            this.showNoSearchResultsMessage = false
        },

        closeFindOriginalModal() {
            this.$refs.findOriginalModal.hide()
        },

        onFindOriginalModalHide() {
            // Allow close (backdrop, Cancel, X) - no prevent
        },

        onFindOriginalModalHidden() {
            this.findOriginalSearchQuery = ''
            this.findOriginalResults = []
            this.selectedTargetOriginal = null
            this.linkAction = 'moveAll'
            this.promotedChildId = null
        },

        async searchOriginals() {
            const query = (this.findOriginalSearchQuery || '').trim()
            if (!query) {
                this.findOriginalResults = []
                this.showNoSearchResultsMessage = false
                return
            }
            this.findOriginalSearching = true
            this.findOriginalResults = []
            try {
                const resp = await recallReviewServices.searchOriginalsForLink(query)
                if (!resp.error && resp.data?.d?.data) {
                    const list = resp.data.d.data || []
                    const currentId = this.recallData?._id
                    this.findOriginalResults = list
                        .filter(item => item._id !== currentId)
                    if(this.findOriginalResults.length === 0) {
                        this.showNoSearchResultsMessage = true
                    }
                    else {
                        this.showNoSearchResultsMessage = false
                    }
                }
            } catch (e) {
                console.error('Search originals error:', e)
                this._showToast('Search failed', { variant: 'danger' })
            } finally {
                this.findOriginalSearching = false
            }
        },

        selectTarget(item) {
            this.selectedTargetOriginal = item
            if (this.hasDuplicateChildren && this.linkAction === 'promoteChild' && this.promoteChildOptions.length) {
                this.promotedChildId = this.promoteChildOptions[0].id
            } else {
                this.promotedChildId = null
            }
        },

        getRecallDisplayName(item) {
            return item.recall_info?.recall_name || item.recall_name || '-'
        },

        getRecallSupplier(item) {
            return item.recall_info?.recalling_firm || item.recalling_firm || '-'
        },

        formatDateForFindOriginal(date) {
            return this.formatDate(date)
        },

        async confirmFindOriginal() {
            if (!this.recallData?._id) return

            let payload
            if (this.linkAction === 'promoteChild' && this.promotedChildId && this.selectedTargetOriginal) {
                // Promote a duplicate to Original; former Original becomes duplicate under selected target
                payload = {
                    action: 'promote',
                    promoted_child_id: this.promotedChildId,
                    target_original_id: this.selectedTargetOriginal._id
                }
            } else if (this.selectedTargetOriginal) {
                const targetId = this.selectedTargetOriginal._id
                if (this.hasDuplicateChildren && this.linkAction === 'moveAll') {
                    payload = { target_original_id: targetId, action: 'moveAll' }
                } else {
                    // Link one recall to an original (no action)
                    payload = { target_original_id: targetId }
                }
            } else {
                return
            }

            try {
                this.isLoading = true
                const resp = await recallReviewServices.linkRecallToOriginal(this.recallData._id, payload)
                if (!resp.error) {
                    this._showToast('Record linked to Original successfully', { variant: 'success' })
                    this.hasSavedChanges = true
                    this.closeFindOriginalModal()
                    await this.refreshData()
                } else {
                    this._showToast(resp.message || 'Failed to link to Original', { variant: 'danger' })
                }
            } catch (e) {
                console.error('Link to original error:', e)
                this._showToast('Failed to link to Original', { variant: 'danger' })
            } finally {
                this.isLoading = false
            }
        },

        // Format date helper
        formatDate(date) {
            if (!date) return '-'
            try {
                // Handle MongoDB $date format: { "$date": "2022-10-18T00:00:00.000Z" }
                const dateValue = date?.$date || date
                // Parse date directly from string to avoid timezone issues
                const dateMatch = String(dateValue).match(/(\d{4})-(\d{2})-(\d{2})/)
                if (dateMatch) {
                    const year = dateMatch[1]
                    const month = dateMatch[2]
                    const day = dateMatch[3]
                    return `${month}/${day}/${year}`
                }
                return '-'
            } catch (error) {
                return '-'
            }
        },

        // Format date time helper
        formatDateTime(date) {
            if (!date) return '-'
            try {
                // Handle MongoDB $date format: { "$date": "2022-10-18T00:00:00.000Z" }
                const dateValue = date?.$date || date
                // Parse date components directly from ISO string to avoid timezone issues
                const dateMatch = String(dateValue).match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
                if (dateMatch) {
                    const year = dateMatch[1]
                    const month = dateMatch[2]
                    const day = dateMatch[3]
                    const hour = parseInt(dateMatch[4], 10)
                    const minute = dateMatch[5]
                    const second = dateMatch[6]
                    // Convert to 12-hour format
                    const period = hour >= 12 ? 'PM' : 'AM'
                    const hour12 = hour % 12 || 12
                    return `${month}/${day}/${year}, ${String(hour12).padStart(2, '0')}:${minute}:${second} ${period}`
                }
                return '-'
            } catch (error) {
                return '-'
            }
        },

        // Navigate to next record
        goToNext() {
            // If field is being edited but no changes saved, just reset editing state
            if (this.isFieldBeingEdited && !this.hasUnsavedChanges) {
                if (this.$refs.detailsTab && typeof this.$refs.detailsTab.resetAllEditingStates === 'function') {
                    this.$refs.detailsTab.resetAllEditingStates()
                }
                this.$emit('nextRecord')
                return
            }
            // If there are actual unsaved changes, show confirmation
            if (this.hasUnsavedChanges) {
                this.pendingNavigation = 'next'
                this.$refs.confirmNavigationModal.show()
            } else {
                this.$emit('nextRecord')
            }
        },

        // Navigate to previous record
        goToPrevious() {
            // If field is being edited but no changes saved, just reset editing state
            if (this.isFieldBeingEdited && !this.hasUnsavedChanges) {
                if (this.$refs.detailsTab && typeof this.$refs.detailsTab.resetAllEditingStates === 'function') {
                    this.$refs.detailsTab.resetAllEditingStates()
                }
                this.$emit('previousRecord')
                return
            }
            // If there are actual unsaved changes, show confirmation
            if (this.hasUnsavedChanges) {
                this.pendingNavigation = 'previous'
                this.$refs.confirmNavigationModal.show()
            } else {
                this.$emit('previousRecord')
            }
        },

        // Handle individual product field update
        handleProductUpdate({ index, field, value }) {
            if (!this.recallData?.recall_info?.product_list) return
            if (index < 0 || index >= this.recallData.recall_info.product_list.length) return
            // Track change only for the specific field that changed
            const oldProduct = this.originalData?.recall_info?.product_list?.[index] || {}
            const originalValue = oldProduct[field]
            // Normalize values for comparison
            let originalValueNormalized = originalValue === undefined || originalValue === null ? '' : originalValue
            let newValueNormalized = value === undefined || value === null ? '' : value
            // Store dates in API format (ISO 8601) in changelog
            if (originalValueNormalized !== newValueNormalized) {
                const productNumber = index + 1
                this.createChangeLog(`product_${productNumber}.${field}`, 'update', originalValueNormalized, newValueNormalized)
            }
            // Update only the specific field in the product
            this.$set(this.recallData.recall_info.product_list[index], field, value)
            this.hasUnsavedChanges = true
        },

        // Handle product update all fields
        handleProductUpdateAll({ index, product }) {
            if (!this.recallData?.recall_info?.product_list) return
            // Track change only for fields that actually changed
            const oldProduct = this.originalData?.recall_info?.product_list?.[index] || {}
            const currentProduct = this.recallData.recall_info.product_list[index] || {}
            Object.keys(product).forEach(key => {
                const originalValue = oldProduct[key]
                const currentValue = currentProduct[key]
                const newValue = product[key]
                // Normalize values for comparison
                let originalValueNormalized = originalValue === undefined || originalValue === null ? '' : originalValue
                let newValueNormalized = newValue === undefined || newValue === null ? '' : newValue
                // Store dates in API format (ISO 8601) in changelog
                if (originalValueNormalized !== newValueNormalized) {
                    const productNumber = index + 1
                    this.createChangeLog(`product_${productNumber}.${key}`, 'update', originalValueNormalized, newValueNormalized)
                }
            })
            // Update the product at the specified index
            this.$set(this.recallData.recall_info.product_list, index, {
                ...this.recallData.recall_info.product_list[index],
                ...product
            })
            this.hasUnsavedChanges = true
        },

        // Handle product delete
        handleProductDelete(index) {
            if (!this.recallData?.recall_info?.product_list) return
            // Track change
            const deletedProduct = this.recallData.recall_info.product_list[index]
            const oldValue = deletedProduct?.name || JSON.stringify(deletedProduct)
            const productNumber = index + 1
            this.createChangeLog(`product_${productNumber}`, 'delete', oldValue, '')
            // Remove the product at the specified index
            this.recallData.recall_info.product_list.splice(index, 1)
            this.hasUnsavedChanges = true
        },

        // Handle product add
        handleProductAdd(product) {
            if (!this.recallData?.recall_info) return
            // Initialize product_list if it doesn't exist
            if (!this.recallData.recall_info.product_list) {
                this.$set(this.recallData.recall_info, 'product_list', [])
            }
            const newIndex = this.recallData.recall_info.product_list.length
            const productNumber = newIndex + 1
            const newValue = product?.name || JSON.stringify(product)
            this.createChangeLog(`product_${productNumber}`, 'add', '', newValue)
            // Add the new product
            this.recallData.recall_info.product_list.push(product)
            this.hasUnsavedChanges = true
        },

        // Handle individual field update
        handleFieldUpdate({ field, value, oldValue }) {
            if (!this.recallData?.recall_info) return

            // Track critical field modifications
            const criticalFields = ['recalling_firm', 'recall_date', 'recall_name']
            if (criticalFields.includes(field) && !this.criticalFieldsModified.includes(field)) {
                this.criticalFieldsModified.push(field)
            }

            // Check if field is a nested property (e.g., recalling_firm_address.street)
            if (field.includes('.')) {
                const [parentField, childField] = field.split('.')
                // Ensure parent object exists
                if (!this.recallData.recall_info[parentField]) {
                    this.$set(this.recallData.recall_info, parentField, {})
                }
                // Track change with oldValue passed from child component
                this.createChangeLog(field, 'update', oldValue, value)
                // Update the nested field
                this.$set(this.recallData.recall_info[parentField], childField, value)
            } else {
                // Track change
                const originalValue = oldValue !== undefined ? oldValue : this.originalData?.recall_info?.[field]
                this.createChangeLog(field, 'update', originalValue, value)
                // Update the field in recall_info
                this.$set(this.recallData.recall_info, field, value)
                // Handle validation errors for mandatory fields - only remove when valid, don't add until Publish
                const mandatoryFields = ['recall_name', 'recalling_firm', 'recall_date', 'description', 'health_risk', 'recall_type', 'recall_category']
                if (mandatoryFields.includes(field)) {
                    const isValid = value && (typeof value !== 'string' || value.trim())
                    const isInErrors = this.validationErrors.includes(field)
                    // Only remove from validation errors if field becomes valid
                    if (isValid && isInErrors) {
                        const index = this.validationErrors.indexOf(field)
                        if (index > -1) {
                            this.validationErrors.splice(index, 1)
                        }
                    }
                }
            }
            this.hasUnsavedChanges = true
        },

        // Handle action update
        handleActionUpdate({ id, index, action }) {
            if (!this.recallData?.recall_info?.actions) return
            // Use the index directly since we already have it
            if (index >= 0 && index < this.recallData.recall_info.actions.length) {
                // Track change
                const oldValue = this.originalData?.recall_info?.actions?.[index]?.value || this.originalData?.recall_info?.actions?.[index]
                const newValue = action.value || action
                this.createChangeLog('actions', 'update', oldValue, newValue)
                this.$set(this.recallData.recall_info.actions, index, action)
                this.hasUnsavedChanges = true
                this._showToast('Action updated successfully', { variant: 'success' })
            }
        },

        // Handle action delete
        handleActionDelete(index) {
            if (!this.recallData?.recall_info?.actions) return
            // Track change
            const deletedAction = this.recallData.recall_info.actions[index]
            const oldValue = deletedAction?.value || deletedAction
            this.createChangeLog('actions', 'delete', oldValue, '')
            this.recallData.recall_info.actions.splice(index, 1)
            this.hasUnsavedChanges = true
            this._showToast('Action deleted successfully', { variant: 'success' })
        },

        // Handle action add
        handleActionAdd(action) {
            if (!this.recallData?.recall_info) return
            if (!this.recallData.recall_info.actions) {
                this.$set(this.recallData.recall_info, 'actions', [])
            }
            const newValue = action.value || action
            this.createChangeLog('actions', 'add', '', newValue)
            this.recallData.recall_info.actions.push(action)
            this.hasUnsavedChanges = true
            this._showToast('Action added successfully', { variant: 'success' })
        },

        // Handle instruction update
        handleInstructionUpdate({ id, index, instruction }) {
            if (!this.recallData?.recall_info?.instructions) return
            if (index >= 0 && index < this.recallData.recall_info.instructions.length) {
                // Track change
                const oldValue = this.originalData?.recall_info?.instructions?.[index]?.value || this.originalData?.recall_info?.instructions?.[index]
                const newValue = instruction.value || instruction
                this.createChangeLog('instructions', 'update', oldValue, newValue)
                this.$set(this.recallData.recall_info.instructions, index, instruction)
                this.hasUnsavedChanges = true
                this._showToast('Instruction updated successfully', { variant: 'success' })
            }
        },

        // Handle instruction delete
        handleInstructionDelete(index) {
            if (!this.recallData?.recall_info?.instructions) return
            // Track change
            const deletedInstruction = this.recallData.recall_info.instructions[index]
            const oldValue = deletedInstruction?.value || deletedInstruction
            this.createChangeLog('instructions', 'delete', oldValue, '')
            this.recallData.recall_info.instructions.splice(index, 1)
            this.hasUnsavedChanges = true
            this._showToast('Instruction deleted successfully', { variant: 'success' })
        },

        // Handle instruction add
        handleInstructionAdd(instruction) {
            if (!this.recallData?.recall_info) return
            if (!this.recallData.recall_info.instructions) {
                this.$set(this.recallData.recall_info, 'instructions', [])
            }
            const newValue = instruction.value || instruction
            this.createChangeLog('instructions', 'add', '', newValue)
            this.recallData.recall_info.instructions.push(instruction)
            this.hasUnsavedChanges = true
            this._showToast('Instruction added successfully', { variant: 'success' })
        },

        // Handle publish button - validate mandatory fields
        async handlePublish() {
            // First validate mandatory fields
            if (!this.recallData?.recall_info) {
                this._showToast('Recall information is missing', { variant: 'danger' })
                return
            }
            const recallInfo = this.recallData.recall_info
            const missingFields = []
            // Check all mandatory fields
            if (!recallInfo.recall_name || !recallInfo.recall_name.trim()) {
                missingFields.push('Recall Name')
            }
            if (!recallInfo.recalling_firm || !recallInfo.recalling_firm.trim()) {
                missingFields.push('Recalling Firm')
            }
            if (!recallInfo.recall_date) {
                missingFields.push('Recall Date')
            }
            if (!recallInfo.description || !recallInfo.description.trim()) {
                missingFields.push('Description')
            }
            if (!recallInfo.health_risk || !recallInfo.health_risk.trim()) {
                missingFields.push('Risk to Health')
            }
            if (!recallInfo.recall_type || !recallInfo.recall_type.trim()) {
                missingFields.push('Recall Type')
            }
            if (!recallInfo.recall_category || !recallInfo.recall_category.trim()) {
                missingFields.push('Recall Group')
            }

            // If validation fails, show errors and stop
            if (missingFields.length > 0) {
                this.validationErrors = [
                    'recall_name',
                    'recalling_firm',
                    'recall_date',
                    'description',
                    'health_risk',
                    'recall_type',
                    'recall_category'
                ].filter(field => {
                    const value = recallInfo[field]
                    return !value || (typeof value === 'string' && !value.trim())
                })
                this.activeTab = 0
                const fieldCount = missingFields.length
                let message = ''
                if (fieldCount === 1) {
                    message = `${missingFields[0]} is required before publishing`
                } else if (fieldCount === 2) {
                    message = `${missingFields[0]} and ${missingFields[1]} are required before publishing`
                } else {
                    const lastField = missingFields[fieldCount - 1]
                    const otherFields = missingFields.slice(0, fieldCount - 1).join(', ')
                    message = `${otherFields}, and ${lastField} are required before publishing`
                }
                this._showToast(message, {
                    variant: 'danger',
                    toaster: 'b-toaster-top-center',
                    autoHideDelay: 5000
                })
                return
            }
            // Clear validation errors if validation passed
            this.validationErrors = []
            // After validation passes, check if there are unsaved changes
            if (this.hasUnsavedChanges) {
                this.$refs.confirmPublishModal.show()
                return
            }
            // Proceed with publishing
            await this.proceedWithPublish()
        },

        // Proceed with publishing (validation already done)
        async proceedWithPublish() {
            // Clear validation errors
            this.validationErrors = []
            try {
                // Add change log entry for status change
                const oldStatus = this.recallData.status || 'PENDING'
                const newStatus = 'PUBLISHED'
                // Create change log entry for publish action
                this.createChangeLog('status', 'publish', oldStatus, newStatus)
                // Add change logs to the payload
                const payload = {
                    ...this.recallData,
                    changelog: this.changeLogs
                }
                const response = await recallReviewServices.publishRecall(
                    this.recallData._id,
                    payload
                )

                if (!response.error) {
                    this._showToast('Recall published successfully', {
                        variant: 'success',
                        toaster: 'b-toaster-top-center'
                    })
                    // Reset change logs after successful publish
                    this.changeLogs = []
                    this.hasSavedChanges = true // Mark that changes were made (status changed to PUBLISHED)
                    // Refresh data to get updated status
                    await this.loadReviewData()
                } else {
                    this._showToast('Failed to publish recall', {
                        variant: 'danger',
                        toaster: 'b-toaster-top-center'
                    })
                }
            } catch (error) {
                console.error('Error publishing recall:', error)
                this._showToast('Failed to publish recall', {
                    variant: 'danger',
                    toaster: 'b-toaster-top-center'
                })
            } finally {
                this.isLoading = false
            }
        },

        // Handle Save & Publish action from confirmation modal
        async handleSaveAndPublish() {
            this.$refs.confirmPublishModal.hide()
            try {
                // First save the changes
                await this.handleSave()
                // Then proceed with publish
                await this.proceedWithPublish()
            } catch (error) {
                console.error('Error in save and publish:', error)
                this._showToast('Failed to save and publish', { variant: 'danger' })
            }
        },

        // Handle Discard & Publish action from confirmation modal
        async handleDiscardAndPublish() {
            this.$refs.confirmPublishModal.hide()
            try {
                // Reload original data to discard changes
                await this.loadReviewData()
                // Reset unsaved changes flag
                this.hasUnsavedChanges = false
                this.changeLogs = []
                // Then proceed with publish
                await this.proceedWithPublish()
            } catch (error) {
                console.error('Error in discard and publish:', error)
                this._showToast('Failed to publish', { variant: 'danger' })
            }
        },

        // Handle Cancel action from confirmation modal
        handleCancelPublish() {
            this.$refs.confirmPublishModal.hide()
        },

        // Reset publish modal state
        resetPublishModal() {
            // Any cleanup if needed
        },

        // Handle Save & Resolve action from confirmation modal
        async handleSaveAndResolve() {
            this.$refs.confirmResolveModal.hide()
            try {
                // First save the changes
                await this.handleSave()
                // Then proceed with resolve
                this.proceedWithResolve()
            } catch (error) {
                console.error('Error in save and resolve:', error)
                this._showToast('Failed to save and resolve', { variant: 'danger' })
            }
        },

        // Handle Discard & Resolve action from confirmation modal
        async handleDiscardAndResolve() {
            this.$refs.confirmResolveModal.hide()
            try {
                // Reload original data to discard changes
                await this.loadReviewData()
                // Reset unsaved changes flag
                this.hasUnsavedChanges = false
                this.changeLogs = []
                // Then proceed with resolve
                this.proceedWithResolve()
            } catch (error) {
                console.error('Error in discard and resolve:', error)
                this._showToast('Failed to resolve', { variant: 'danger' })
            }
        },

        // Handle Cancel action from resolve confirmation modal
        handleCancelResolve() {
            this.$refs.confirmResolveModal.hide()
        },

        // Reset resolve modal state
        resetResolveModal() {
            // Any cleanup if needed
        },

        // Override _showToast to prevent duplicate toasts
        _showToast(message, options = {}) {
            // Create a unique key for this toast message
            const toastKey = `${message}_${options.variant || 'default'}`
            // Check if this toast is already showing
            if (this.activeToasts.has(toastKey)) {
                return
            }
            // Add to active toasts
            this.activeToasts.add(toastKey)
            // Show the toast
            this.$bvToast.toast(message, {
                title: options.title || 'Notification',
                variant: options.variant || 'info',
                solid: true,
                toaster: options.toaster || 'b-toaster-top-center',
                autoHideDelay: options.autoHideDelay || 3000,
                ...options
            })
            // Remove from active toasts after it's hidden
            setTimeout(() => {
                this.activeToasts.delete(toastKey)
            }, (options.autoHideDelay || 3000) + 500)
        },

        handleResponseFieldUpdateMultiple(updateResponseData, index){
            // updateResponseData is an array of objects with { fieldId, field, value }
            if (!Array.isArray(updateResponseData) || !updateResponseData.length) return

            // Process each field update in the array
            updateResponseData.forEach(update => {
                const { fieldId, field, value } = update
                if (!fieldId || !field) return

                // Navigate to the response in recall_info.responses[index]
                if (!this.recallData?.recall_info?.response_form || !Array.isArray(this.recallData.recall_info.response_form)) return
                if (index < 0 || index >= this.recallData.recall_info.response_form.length) return

                const response = this.recallData.recall_info.response_form[index]
                if (!response) return

                // Track change only for the specific field that changed
                const oldResponse = this.originalData?.recall_info?.response_form?.[index] || {}
                const originalValue = oldResponse[field]

                // Normalize values for comparison
                let originalValueNormalized = originalValue === undefined || originalValue === null ? '' : originalValue
                let newValueNormalized = value === undefined || value === null ? '' : value

                // Store values in changelog if they changed
                if (originalValueNormalized !== newValueNormalized) {
                    this.createChangeLog(`response_form.${field}`, 'update', originalValueNormalized, newValueNormalized)
                }

                // Update the specific field in the response
                this.$set(this.recallData.recall_info.response_form[index], field, value)
            })
            this.hasUnsavedChanges = true
        },
    },
}
